import React from "react";
import Icon from "./Icon";
import { useServerVersion } from "../../hooks/useServerVersion";
import { CLIENT_VERSION, formatVersion, VersionInfo } from "../../utils/appVersion";

type AppVersionProps = {
  // "light" sits on the light sidebar, "dark" on the login background
  tone?: "light" | "dark";
  // Narrow layout for the collapsed sidebar: version only
  compact?: boolean;
  className?: string;
};

const isRelease = (info: VersionInfo) => /^\d/.test(info.version);
const versionLabel = (info: VersionInfo) => (isRelease(info) ? `v${info.version}` : info.version);

/**
 * Affiche discrètement la version de l'application : pastille de statut, version et commit.
 * La pastille devient orange pour un build de dev, ou en cas de client et serveur dans des versions
 * différentes (déploiement partiel).
 */
export default function AppVersion({ tone = "light", compact = false, className = "" }: AppVersionProps) {
  const { serverVersion } = useServerVersion();
  const client = CLIENT_VERSION;
  const mismatch = serverVersion !== undefined && formatVersion(serverVersion) !== formatVersion(client);
  const dark = tone === "dark";

  const dotColor = mismatch || !isRelease(client) ? "bg-accent" : dark ? "bg-[#21B7B3]" : "bg-primary";
  const tooltip = [
    `Client: ${formatVersion(client)}`,
    `Server: ${serverVersion ? formatVersion(serverVersion) : "unavailable"}`,
    ...(mismatch ? ["Client and server versions differ"] : []),
  ].join("\n");

  return (
    <div
      className={`flex items-center justify-center gap-1.5 font-display text-[11px] ${
        dark ? "text-white/70" : "text-primary-dark/60"
      } ${className}`}
      data-testid="app-version"
      title={tooltip}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotColor}`} />
      <span className="font-medium">{versionLabel(client)}</span>
      {!compact && client.commit && <span className="font-mono opacity-60">{client.commit.slice(0, 7)}</span>}
      {!compact && mismatch && (
        <span className="flex items-center text-accent">
          <Icon icon="warn" />
          API {versionLabel(serverVersion)}
        </span>
      )}
    </div>
  );
}
